import * as yup from 'yup'
import { useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Button } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded'

import useAPIFetch from '@hooks/useAPIFetch'
import Header from '@components/configurations/header'
import RecipientService from '@services/users/recipient.service'
import ValidationToast from '@components/configurations/form/ValidationToast'
import { useMeta } from '@contexts/Meta'
import { ReactComponent as SmsIcon } from '@assets/sideMenuIcon/info.svg'
import RecipientInfoForm from '@components/configurations/users_config/sms_recipients/form/recipientInfoForm'
import SmsGroupSelection from '@components/configurations/users_config/sms_recipients/form/smsGroupSelection'

interface EditRecipientFormData {
	contact_id?: string
	name: string
	position: string
	contact_infos?: {
		contact_info_id?: string
		channel_type?: string
		info: string
	}[]
	// For form binding
	phone: string
	groups?: {
		group_id: string
		building: string
	}[]
	selected_groups?: string[]
	original_groups?: string[]
}

export const EditSmsRecipient = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const [, { setMeta }] = useMeta()
	const { enqueueSnackbar } = useSnackbar()
	const { id: contactId } = useParams<string>()
	const { setRequest, isLoading } = useAPIFetch()
	const [groupMapping, setGroupMapping] = useState<Record<string, string>>({}) // building name -> group_id
	const isCreateMode = !contactId

	const validationSchema = yup
		.object()
		.shape({
			contact_id: yup.string(),
			name: yup.string().required('Name is required'),
			position: yup.string().required('Position is required'),
			phone: yup
				.string()
				.required('Phone is required')
				.matches(/^[0-9]+$/, 'Phone number must contain only numbers')
				.length(8, 'Please enter a valid 8-digit Hong Kong mobile phone number')
				.matches(/^[45689]/, 'Please enter a valid 8-digit Hong Kong mobile phone number'),
			groups: yup.array(),
			selected_groups: yup.array().min(1, 'At least one SMS group is required'),
		})
		.required()

	const methods = useForm<EditRecipientFormData>({
		resolver: yupResolver(validationSchema) as any,
		mode: 'all',
		reValidateMode: 'onChange',
		criteriaMode: 'all',
	})

	const {
		reset,
		handleSubmit,
		control,
		formState: { dirtyFields },
	} = methods

	useEffect(() => {
		loadGroups()
		if (!isCreateMode) {
			reload()
		}
	}, [contactId])

	const loadGroups = async () => {
		try {
			const groupsData = await RecipientService.GetAllGroups()
			const mapping: Record<string, string> = {}
			groupsData.forEach(group => {
				mapping[group.building] = group.id
			})
			
			setGroupMapping(mapping)
		} catch (error) {
			console.error('Failed to load groups:', error)
		}
	}

	useEffect(() => {
		setMeta({ isLoading })
	}, [isLoading])

	const onSubmit = async (data: EditRecipientFormData) => {
		console.log('Submit data:', data)

		setRequest({
			callback: async () => {
				try {
					let createdContactId = contactId || data.contact_id

					if (isCreateMode) {
					
						const contactData = {
							building: '', // Leave empty as per requirements
							name: data.name,
							position: data.position || '',
							contact_infos: [
								{
									channel_type: 'sms', 
									info: data.phone || ''
								}
							]
						}

						
						const createdContact: any = await RecipientService.CreateContact(contactData)
					
						createdContactId = createdContact?.contact_id || createdContact?.id || (typeof createdContact === 'string' ? createdContact : null)

						if (!createdContactId || typeof createdContactId !== 'string') {
							throw new Error('Failed to get contact ID from creation response')
						}

					
						const selectedGroups = data.selected_groups || []
						const promises: Promise<any>[] = []

						for (const buildingName of selectedGroups) {
							const groupId = groupMapping[buildingName]
							if (groupId) {
								
								promises.push(RecipientService.AddContactToGroup(groupId, [createdContactId]))
							} else {
								console.warn(`Group ID not found for building: ${buildingName}`)
							}
						}

					
						await Promise.all(promises)

						enqueueSnackbar('SMS Recipient created successfully.', { variant: 'success' })
						setMeta({ isLoading: false })
						navigate('/configurations/users_config/sms_recipients')
					} else {
					
						const promises: Promise<any>[] = []

					
						if (dirtyFields.name || dirtyFields.position) {
							const contactData: any = {}
							if (dirtyFields.name) contactData.name = data.name
							if (dirtyFields.position) contactData.position = data.position

				
							promises.push(RecipientService.UpdateContact(data.contact_id ?? '', contactData))
						}

				
						if (dirtyFields.phone && data.contact_infos && data.contact_infos.length > 0) {
							const contactInfoData = {
								info: data.phone ?? '',
								channel_type: data.contact_infos[0].channel_type || 'sms'
							}
						
							promises.push(
								RecipientService.UpdateContactInfo(
									data.contact_infos[0].contact_info_id ?? '',
									contactInfoData
								)
							)
						}

						// 3. Handle SMS Group changes
						const originalGroups = data.original_groups || []
						const selectedGroups = data.selected_groups || []

					
						const groupsToAdd = selectedGroups.filter(g => !originalGroups.includes(g))
						
						const groupsToRemove = originalGroups.filter(g => !selectedGroups.includes(g))

						
						
						for (const buildingName of groupsToAdd) {
							const groupId = groupMapping[buildingName]
							if (groupId) {
								
								promises.push(RecipientService.AddContactToGroup(groupId, [data.contact_id ?? '']))
							} else {
								console.warn(`Group ID not found for building: ${buildingName}`)
							}
						}

				
						for (const buildingName of groupsToRemove) {
							const groupId = groupMapping[buildingName]
							if (groupId) {
								
								promises.push(RecipientService.RemoveContactFromGroup(groupId, data.contact_id || ''))
							} else {
								console.warn(`Group ID not found for building: ${buildingName}`)
							}
						}

						await Promise.all(promises)

						enqueueSnackbar('SMS Recipient updated successfully.', { variant: 'success' })
						setMeta({ isLoading: false })
						navigate(-1)
					}
				} catch (error: any) {
					console.error(isCreateMode ? 'Create error:' : 'Update error:', error)
					enqueueSnackbar(error?.response?.data?.message || (isCreateMode ? 'Failed to create recipient' : 'Failed to update recipient'), {
						variant: 'error',
					})
					setMeta({ isLoading: false })
				}
			},
		})
	}

	const onError = (errors: any) => {
		console.log('Validation errors:', errors)
	}

	const reload = async () => {
		setRequest({
			callback: async () => {
				if (contactId) {
					await RecipientService.GetRecipientById(contactId)
						.then(async (f) => {
							
							const selectedGroups = f.groups?.map(g => g.building) || []
							
							const phone = f.contact_infos?.[0]?.info || ''

							const formData = {
								...f,
								phone: phone,
								selected_groups: selectedGroups,
								original_groups: selectedGroups, 
							}

							
							reset(formData)
						})
						.catch((err) => {
							enqueueSnackbar(err?.response?.data?.message || 'Failed to load recipient', {
								variant: 'error',
							})
							navigate('/configurations/users_config/sms_recipients')
						})
				}
			},
		})
	}

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<ArrowBackIosRoundedIcon
					fontSize={'large'}
					sx={{ cursor: 'pointer', color: '#707070' }}
					onClick={() => {
						navigate(`/configurations/users_config/sms_recipients`)
					}}
				/>
				<Header
					title={isCreateMode ? t('Create SMS Recipient') : t('Edit SMS Recipient')}
					icon={<SmsIcon fill={'#707070'} />}
				/>
			</Box>
			<Box component="form" onSubmit={handleSubmit(onSubmit, onError)} sx={{ marginX: '45px' }}>
				<FormProvider {...methods}>
					<RecipientInfoForm />
					<SmsGroupSelection />

					<Box
						sx={{
							display: 'flex',
							justifyContent: 'flex-end',
							gap: '12px',
							marginTop: '24px',
						}}
					>
						<Button variant="contained" color="defaultButton" type="submit" disabled={isLoading}>
							{t('Confirm')}
						</Button>
						<Button
							variant="contained"
							color="error"
							onClick={() => navigate(-1)}
							disabled={isLoading}
						>
							{t('Cancel')}
						</Button>
					</Box>
				</FormProvider>
				<ValidationToast control={control} />
			</Box>
		</>
	)
}
